import { ColumnHelper } from '@tanstack/table-core';
import { Badge } from '@givebutter/primitives/badge';
import { maskWholeNumber } from '@givebutter/util';
import { faEnvelope, faPalette, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionColumn } from 'assets/js/pages/engage/components/TableColumns/ActionColumn';
import { StatsColumn } from 'assets/js/pages/engage/components/TableColumns/StatsColumn';
import * as Table from 'components/table';
import { CellDataOrEmpty } from 'components/table/Table';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { EngageMessage } from 'types/domain';

enum columnIds {
    CreatedOn = 'created_on',
    Creator = 'creator_full_name',
    Name = 'internal_name',
    RecipientCount = 'recipient_count',
    Sender = 'sender',
    SendingOn = 'sending_on',
    SentOn = 'sent_on',
    Stats = 'stats',
    Type = 'editor',
    UpdatedOn = 'updated_on',
}

const createMessageColumns = (columnHelper: ColumnHelper<EngageMessage>) => {
    return {
        expand: columnHelper.display({
            id: Table.Constants.Expand,
            enableHiding: false,
            cell: info => <Table.RowExpand row={info.row} />,
        }),
        name: columnHelper.accessor('internal_name', {
            id: columnIds.Name,
            header: 'Name',
            cell: info => <p className={'font-medium'}>{info.getValue()}</p>,
            meta: {
                className: '[&>*]:w-[250px]',
            },
        }),
        type: columnHelper.accessor('editor', {
            id: columnIds.Type,
            header: 'Type',
            enableSorting: false,
            cell: info => {
                const editor = info.getValue();
                return (
                    <Badge size="sm">
                        <FontAwesomeIcon
                            className="text-gray-400"
                            icon={editor === 'basic' ? faEnvelope : faPalette}
                        />
                        {editor === 'basic' ? 'Classic' : 'Visual'}
                    </Badge>
                );
            },
        }),
        createdAt: columnHelper.accessor('created_at', {
            cell: info => (
                <p>{isoDateFromServer(info.getValue()).toLocaleString(DateTime.DATE_MED)}</p>
            ),
            header: 'Creation Date',
            id: columnIds.CreatedOn,
            meta: {
                sortType: 'date',
            },
        }),
        recipients: columnHelper.accessor('recipient_count', {
            id: columnIds.RecipientCount,
            header: 'Recipients',
            enableSorting: false,
            cell: info => <p>{maskWholeNumber(info.getValue())}</p>,
        }),
        estimatedRecipients: columnHelper.accessor('recipient_count', {
            id: columnIds.RecipientCount,
            header: () => (
                <div className={'flex items-center gap-1.5'}>
                    <p>Estimated recipients</p>
                    <Tooltip.Root>
                        <Tooltip.Trigger
                            aria-label="Learn more about estimated recipients"
                            className={'flex items-center justify-center'}
                            asChild
                        >
                            <FontAwesomeIcon size={'sm'} icon={faQuestionCircle} />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse={true}>
                                <p>
                                    Messages will be sent to contacts matching your filters at the
                                    time of sending, less those who have not opted-in or have
                                    unsubscribed.
                                </p>
                                <Tooltip.Arrow inverse={true} />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            ),
            enableSorting: false,
            cell: info => <p>{maskWholeNumber(info.getValue())}</p>,
        }),
        creator: columnHelper.accessor('creator_full_name', {
            cell: () => <CellDataOrEmpty />,
            header: 'Created by',
            id: columnIds.Creator,
        }),
        updatedAt: columnHelper.accessor('updated_at', {
            header: 'Last edited',
            id: columnIds.UpdatedOn,
            cell: info => (
                <p>{isoDateFromServer(info.getValue()).toLocaleString(DateTime.DATE_MED)}</p>
            ),
            meta: {
                sortType: 'date',
            },
        }),
        sender: columnHelper.accessor('sender.value', {
            cell: () => <CellDataOrEmpty />,
            enableSorting: false,
            header: 'Sent By',
            id: columnIds.Sender,
        }),
        sendingAt: columnHelper.accessor('sending_at', {
            header: 'Scheduled for',
            id: columnIds.SendingOn,
            cell: info => {
                const sendingAt = info.getValue();
                return sendingAt ? (
                    <p>{isoDateFromServer(sendingAt).toLocaleString(DateTime.DATE_MED)}</p>
                ) : (
                    <EmptyData />
                );
            },
            meta: {
                sortType: 'date',
            },
        }),
        sentAt: columnHelper.accessor('sent_at', {
            cell: info => {
                const sentAt = info.getValue();
                return sentAt ? (
                    <p>{isoDateFromServer(sentAt).toLocaleString(DateTime.DATE_MED)}</p>
                ) : (
                    <EmptyData />
                );
            },
            header: 'Sent Date',
            id: columnIds.SentOn,
            meta: {
                sortType: 'date',
            },
        }),
        states: columnHelper.display({
            id: columnIds.Stats,
            cell: info => {
                return <StatsColumn info={info} />;
            },
        }),
        action: columnHelper.display({
            id: Table.Constants.Actions,
            cell: info => <ActionColumn info={info} />,
            meta: {
                ignoreLink: true,
            },
        }),
    };
};

export { columnIds, createMessageColumns };
